import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, Plus, Eye, Mail, Phone, Building, Trash2, KeyRound, MoreVertical, Pencil, Send, AlertCircle, Lock, EyeOff, X } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const ClientList = () => {
  const { token, admin: currentAdmin } = useAdminAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (email, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir o cliente "${name}"?\n\nEsta ação também removerá todos os documentos, contratos e notificações associados.`)) {
      return;
    }

    setActionLoading(email);
    try {
      await axios.delete(`${API}/admin/clients/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(clients.filter(c => c.email !== email));
      alert('Cliente excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Erro ao excluir cliente');
    } finally {
      setActionLoading(null);
      setActionMenuOpen(null);
    }
  };

  const handleResendFirstAccess = async (email, name) => {
    if (!window.confirm(`Reenviar email de primeiro acesso para "${name}" (${email})?`)) {
      return;
    }

    setActionLoading(email);
    try {
      await axios.post(`${API}/admin/clients/${encodeURIComponent(email)}/resend-invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Email de primeiro acesso reenviado com sucesso!');
    } catch (error) {
      console.error('Error resending first access:', error);
      alert(error.response?.data?.detail || 'Erro ao reenviar email');
    } finally {
      setActionLoading(null);
      setActionMenuOpen(null);
    }
  };

  const handleSendPasswordReset = async (email, name) => {
    if (!window.confirm(`Enviar email de redefinição de senha para "${name}" (${email})?`)) {
      return;
    }

    setActionLoading(email);
    try {
      await axios.post(`${API}/admin/clients/${encodeURIComponent(email)}/send-password-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Email de redefinição de senha enviado com sucesso!');
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert('Erro ao enviar email de redefinição');
    } finally {
      setActionLoading(null);
      setActionMenuOpen(null);
    }
  };

  // Open password modal
  const openPasswordModal = (client) => {
    setSelectedClient(client);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPassword(false);
    setShowPasswordModal(true);
    setActionMenuOpen(null);
  };

  // Close password modal
  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedClient(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  // Handle set password
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 6) {
      setPasswordError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(
        `${API}/admin/clients/${encodeURIComponent(selectedClient.email)}/set-password`,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setClients(clients.map(c => 
        c.email === selectedClient.email 
          ? { ...c, password_set: true, hashed_password: 'set' } 
          : c
      ));
      
      alert('Senha definida com sucesso!');
      closePasswordModal();
    } catch (error) {
      console.error('Error setting password:', error);
      setPasswordError(error.response?.data?.detail || 'Erro ao definir senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check if client needs first access
  const needsFirstAccess = (client) => {
    return !client.password_set && (!client.hashed_password || client.hashed_password === '');
  };

  const filteredClients = clients.filter(client =>
    client.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    client.email?.toLowerCase().includes(search.toLowerCase()) ||
    client.company?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="text-arcano-blue" />
            Clientes
          </h1>
          <p className="text-gray-600 text-sm md:text-base">Gerencie todos os clientes cadastrados</p>
        </div>
        <Link
          to="/admin/clients/new"
          className="flex items-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors text-sm md:text-base w-full sm:w-auto justify-center"
        >
          <Plus size={20} />
          Novo Cliente
        </Link>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, email ou empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhum cliente encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nome</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Empresa</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Telefone</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{client.full_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={16} />
                        {client.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {client.company ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building size={16} />
                          {client.company}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {client.phone ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} />
                          {client.phone}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                          client.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {client.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                        {needsFirstAccess(client) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                            <AlertCircle size={12} />
                            Aguardando senha
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/clients/${encodeURIComponent(client.email)}`}
                          className="inline-flex items-center gap-1 text-arcano-blue hover:underline"
                        >
                          <Eye size={16} />
                          Ver
                        </Link>
                        <Link
                          to={`/admin/clients/${encodeURIComponent(client.email)}/edit`}
                          className="inline-flex items-center gap-1 text-green-600 hover:underline"
                        >
                          <Pencil size={16} />
                          Editar
                        </Link>
                        
                        {/* Actions Menu */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuOpen(actionMenuOpen === client.email ? null : client.email);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                            disabled={actionLoading === client.email}
                          >
                            {actionLoading === client.email ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-arcano-blue"></div>
                            ) : (
                              <MoreVertical size={18} className="text-gray-500" />
                            )}
                          </button>
                          
                          {actionMenuOpen === client.email && (
                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                              <div className="py-1">
                                {needsFirstAccess(client) ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResendFirstAccess(client.email, client.full_name);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50"
                                  >
                                    <Send size={16} />
                                    Reenviar email de primeiro acesso
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSendPasswordReset(client.email, client.full_name);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <KeyRound size={16} />
                                    Enviar redefinição de senha
                                  </button>
                                )}
                                {currentAdmin?.is_super_admin && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPasswordModal(client);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                                  >
                                    <Lock size={16} />
                                    Definir senha manualmente
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(client.email, client.full_name);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                  Excluir cliente
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Total */}
      <div className="text-right text-gray-600">
        Total: {filteredClients.length} cliente(s)
      </div>

      {/* Set Password Modal */}
      {showPasswordModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" data-testid="set-password-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Lock className="text-purple-600" size={24} />
                Definir Senha
              </h2>
              <button
                onClick={closePasswordModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSetPassword} className="p-6 space-y-4">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">Cliente:</p>
                <p className="font-medium text-gray-900">{selectedClient.full_name}</p>
                <p className="text-sm text-gray-500">{selectedClient.email}</p>
              </div>

              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {passwordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                    required
                    minLength={6}
                    placeholder="Mínimo 6 caracteres"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                    required
                    minLength={6}
                    placeholder="Digite novamente"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-password-btn"
                >
                  {passwordLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Lock size={18} />
                      Definir Senha
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;
